
import { CalendarEvent } from '../../types';
import { fromLocalISOString } from '@/utils/timezoneUtils';

// Helper: compare two dates by local yyyy-mm-dd for robust day matching across timezones
export function isSameDayLocal(dateA: Date, dateB: Date): boolean {
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

// Helper function to generate day schedules more robustly for date-only matching
export const generateDaySchedules = (startDate: Date, endDate: Date, schedules: CalendarEvent[]): any[] => {
  const daySchedules = [];
  let currentDate = new Date(startDate);

  // End-of-day for last day to include all events until the end of the last day
  const endDay = new Date(endDate);
  endDay.setHours(23, 59, 59, 999);

  while (currentDate <= endDay) {
    const eventsForDay = schedules.filter((schedule: any) => {
      // Parse schedule.start_date with fallback for string values:
      let scheduleDate;
      if (schedule.start_date instanceof Date) {
        scheduleDate = schedule.start_date;
      } else {
        scheduleDate = fromLocalISOString(schedule.start_date);
      }
      return isSameDayLocal(scheduleDate, currentDate);
    });

    daySchedules.push({
      date: new Date(currentDate),
      events: eventsForDay,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return daySchedules;
};

// Format last names to display last name first
export const formatLastNames = (names?: string[]) => {
  if (!names || names.length === 0) return '';

  return names.map(name => {
    const nameParts = name.trim().split(' ');
    if (nameParts.length > 1) {
      const lastName = nameParts.pop() || '';
      return `${lastName} ${nameParts.join(' ')}`;
    }
    return name;
  }).join(', ');
};

// Format guard names with & for 2 or +X for more
export const formatGuardNames = (guardNames?: string[]) => {
  if (!guardNames || guardNames.length === 0) return '';
  if (guardNames.length === 1) {
    return guardNames[0];
  } else if (guardNames.length === 2) {
    return `${guardNames[0]} & ${guardNames[1]}`;
  } else {
    return `${guardNames[0]} +${guardNames.length - 1}`;
  }
};

// Calculate visible days slice
export const getVisibleDaySchedules = (daySchedules: any[], startIndex: number, count: number) => {
  return daySchedules.slice(startIndex, startIndex + count);
};

// Group events by title for row view
export const groupEventsByTitle = (daySchedules: any[]) => {
  const groupedEvents: Record<string, Record<string, CalendarEvent[]>> = {};
  
  // Get unique titles from all events
  const allEvents = daySchedules.flatMap((day: any) => day.events);
  const uniqueTitles = [...new Set(allEvents.map((event: any) => event.title))].sort();
  
  // Initialize structure
  uniqueTitles.forEach(title => {
    groupedEvents[title] = {};
    daySchedules.forEach((day: any) => {
      const dateKey = day.date.toISOString();
      groupedEvents[title][dateKey] = [];
    });
  });
  
  // Populate with events
  daySchedules.forEach((day: any) => {
    const dateKey = day.date.toISOString();
    day.events.forEach((event: any) => {
      if (groupedEvents[event.title]) {
        groupedEvents[event.title][dateKey].push(event);
      }
    });
  });
  
  return { groupedEvents, uniqueTitles };
};

// Calculate optimal column width based on device and number of days
export const calculateOptimalColumnWidth = (daysCount: number, isMobile: boolean): string => {
  const baseWidth = isMobile ? 120 : 150;
  return `${baseWidth}px`;
};

// Get calendar grid layout styles based on days count
export const getCalendarGridStyles = (daysCount: number): React.CSSProperties => {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${daysCount}, minmax(120px, 1fr))`,
    gap: '4px'
  };
};
